import { DataTableSortableColumn, ColumnDef } from '@ui/components';
import { ColumnConfiguration } from '@interfaces/models';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

import { cellTemplates } from './cell-templates';

export interface AutoColumnsConfig<T> {
  columns: ColumnConfiguration[];
  actions?: ColumnDef<T>;
  maxColumnWidth?: number;
}

export function useAutoColumns<T = unknown>({
  maxColumnWidth,
  columns,
  actions,
}: AutoColumnsConfig<T[]>) {
  const { t } = useTranslation();

  const indexDictionary = useMemo(() => {
    return columns.reduce(
      (acc, columnConfig, index) => {
        if (!columnConfig.column) return acc;
        acc[columnConfig.column] = index;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [columns]);

  const getColumnHeader = useCallback(
    (columnConfig: ColumnConfiguration): ColumnDef<T[]>['header'] => {
      return columnConfig.isSortable
        ? ({ column }) => (
            <DataTableSortableColumn
              label={t(`AUTO_COLUMNS.${columnConfig.column}`)}
              column={column}
            />
          )
        : t(`AUTO_COLUMNS.${columnConfig.column}`);
    },
    [t],
  );

  const getColumnCell = useCallback(
    (columnConfig: ColumnConfiguration): ColumnDef<T[]>['cell'] => {
      const columnType = columnConfig.columnType as keyof typeof cellTemplates;
      if (!cellTemplates[columnType]) return '-';
      return ({ row, column }) => {
        const index = indexDictionary[column.id]!;
        const value = row.original[index] as T;
        return cellTemplates[columnType](value);
      };
    },
    [indexDictionary],
  );

  const autoColumns: ColumnDef<T[]>[] = columns
    .filter((columnConfig) => columnConfig.column && !!columnConfig.isVisible)
    .map((columnConfig) => {
      return {
        id: columnConfig.column,
        accessorKey: String(indexDictionary[columnConfig.column!]),
        header: getColumnHeader(columnConfig),
        enableHiding: columnConfig.isVisible,
        cell: getColumnCell(columnConfig),
        maxSize: maxColumnWidth,
      };
    });

  const columnDefinitions = useMemo(() => {
    return [...autoColumns, ...(actions ? [actions] : [])];
  }, [autoColumns, actions]);

  return {
    columnDefinitions,
  };
}
